import { NoStockLocationNotice } from '../../app/no-stock-location-notice.js';
import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { useCollectionView } from '../../app/use-collection-view.js';
import {
  PackageCheck,
  CalendarCheck,
  History,
  Gift,
  PencilLine,
  CalendarPlus,
  Undo2,
  Package,
  ExternalLink,
} from 'lucide-react';
import { civilDateOf, formatDateTime } from '../../lib/time-zone.js';
import { useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Button,
  Input,
  Modal,
  Textarea,
  FormErrorSummary,
  useSnackbar,
  Select,
  IconButton,
  StatusPill,
  DatePicker,
  addMonths,
} from '@hue/design-system';
import type { PrepaidBalance, PrepaidAction } from '@hue/contracts';
import { getApiError } from '../../lib/api.js';
import {
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  SortableColumn,
  SortBar,
  EmptyCard,
} from '../../app/data-view.js';
import { prepaidApi } from './api.js';

const statuses: Record<PrepaidBalance['status'], string> = {
  AWAITING: 'Aguardando pagamento',
  AVAILABLE: 'Disponível',
  EXHAUSTED: 'Esgotado',
  EXPIRED: 'Vencido',
  BLOCKED: 'Bloqueado por estorno',
  CLOSED: 'Encerrado',
};
const types = {
  RELEASE: 'Liberação',
  USE: 'Uso',
  UNDO: 'Uso desfeito',
  BONUS: 'Bonificação',
  CORRECTION: 'Correção',
  EXTEND: 'Prorrogação',
  BLOCK: 'Bloqueio',
  CLOSE: 'Encerramento',
};
const day = (value: string | null) =>
  value ? value.split('-').reverse().join('/') : 'Sem validade';
const ruleLabels = {
  cardRelease: 'Liberação no cartão',
  partialRelease: 'Pagamento parcial',
  validityMonths: 'Validade em meses',
  extensionMode: 'Forma de prorrogação',
  extensionMonths: 'Limite em meses',
  noShowConsumes: 'Falta consome',
  lateCancellationHours: 'Antecedência em horas',
};
const values: Record<string, string> = {
  AUTHORIZED: 'Na autorização',
  CONFIRMED: 'Na confirmação',
  FULL: 'Só no total',
  PROPORTIONAL: 'Na proporção',
  DISABLED: 'Não permite',
  CUMULATIVE: 'Limite acumulado',
  PER_REQUEST: 'Por solicitação',
  true: 'Sim',
  false: 'Não',
};
const origins = { COMPANY: 'Empresa', UNIT: 'Unidade', PACKAGE: 'Pacote' };

const actionIcons: Record<string, React.ReactNode> = {
  'Abrir retirada deste produto': <ExternalLink />,
  'Retirar produto': <PackageCheck />,
  'Usar sessão': <CalendarCheck />,
  'Histórico e regras': <History />,
  Bonificar: <Gift />,
  'Corrigir saldo': <PencilLine />,
  'Prorrogar validade': <CalendarPlus />,
  'Desfazer uso': <Undo2 />,
};
const actionHelp: Record<string, string> = {
  'Abrir retirada deste produto':
    'Na ficha do cliente, abre Retiradas de produtos com este saldo já selecionado, preservando a pessoa beneficiária e o item comprado. Abrir o atalho não registra uma entrega.',
  'Retirar produto':
    'Registra a entrega deste produto do pacote ao cliente. Desconta o saldo e baixa o estoque juntos; informe quantidade, local e lote quando exigido.',
  'Usar sessão':
    'Registra manualmente uma sessão utilizada. Se o atendimento já usa o pacote na Agenda, conclua por lá para evitar consumo duplicado.',
  'Histórico e regras':
    'Consulte os lançamentos, os motivos e as regras preservadas da venda. Um uso pode ser desfeito no histórico, conforme suas permissões.',
  Bonificar:
    'Acrescenta unidades gratuitas deste mesmo item ao saldo existente, sem gerar venda ou receita. Mantém a validade do saldo e exige motivo.',
  'Corrigir saldo':
    'Acrescenta ou retira unidades para corrigir um lançamento anterior, que deve ser identificado. Não altera o produto, o pacote vendido ou seu preço. Exige motivo e preserva o histórico.',
  'Prorrogar validade':
    'Amplia a validade em meses civis, dentro do limite preservado neste saldo. Não acrescenta unidades e exige motivo.',
  'Desfazer uso':
    'Reverte integralmente um uso anterior, sem apagá-lo. Para produto, devolve o saldo e estorna a saída de estoque na mesma operação.',
};
function BalanceStatus({ balance }: { balance: PrepaidBalance }) {
  return (
    <StatusPill
      tone={
        balance.status === 'AVAILABLE'
          ? 'positive'
          : balance.status === 'AWAITING'
            ? 'warning'
            : balance.status === 'BLOCKED' || balance.status === 'EXPIRED'
              ? 'danger'
              : 'neutral'
      }
    >
      {statuses[balance.status]}
    </StatusPill>
  );
}

export function ClientBalances({
  clientId,
  canUse,
  canAdjust,
  activeUnitId,
  canWithdraw = false,
  balanceId,
  onClearBalance,
  onOpenWithdrawal,
}: {
  clientId: string;
  balanceId?: string | undefined;
  onClearBalance?: (() => void) | undefined;
  onOpenWithdrawal?: ((balanceId: string) => void) | undefined;
  canWithdraw?: boolean | undefined;
  canUse: boolean;
  canAdjust: boolean;
  activeUnitId: string | null;
}) {
  const [showHelp, setShowHelp] = useState(false);
  const view = useCollectionView() === 'list' ? 'table' : 'cards';
  const [sort, setSort] = useState<'itemName' | 'packageName' | 'available' | 'expiresOn'>(
    'itemName',
  );
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState<{
    balance: PrepaidBalance;
    type: PrepaidAction['type'];
    eventId?: string;
  }>();
  const [details, setDetails] = useState<PrepaidBalance>();
  const query = useQuery({
    queryKey: ['company', 'prepaid', 'client', clientId, search, limit, offset, balanceId],
    queryFn: () =>
      prepaidApi.list(clientId, { search, limit, offset, ...(balanceId ? { balanceId } : {}) }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const client = useQueryClient();
  const balances = [...(query.data?.balances ?? [])].sort((a, b) => {
    const left = a[sort] ?? '';
    const right = b[sort] ?? '';
    const compared =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right), 'pt-BR', { numeric: true });
    return direction === 'asc' ? compared : -compared;
  });
  const columns = [
    { sort: 'itemName', label: 'Item' },
    { sort: 'packageName', label: 'Pacote' },
    { sort: 'available', label: 'Disponíveis' },
    { sort: 'expiresOn', label: 'Validade' },
  ] as const;
  function toggleSort(next: typeof sort) {
    setSort(next);
    setDirection(next === sort && direction === 'asc' ? 'desc' : 'asc');
  }
  const pagination = (
    <Pagination
      total={query.data?.total ?? 0}
      limit={limit}
      offset={offset}
      onOffsetChange={setOffset}
      itemLabel={(count) => (count === 1 ? 'saldo' : 'saldos')}
    />
  );
  const actions = (balance: PrepaidBalance) => (
    <div className="flex flex-wrap gap-1">
      {balance.kind === 'PRODUCT' && onOpenWithdrawal ? (
        <IconButton
          label="Abrir retirada deste produto"
          icon={<ExternalLink />}
          onClick={() => onOpenWithdrawal(balance.id)}
        />
      ) : null}
      {(canUse && balance.kind === 'SERVICE') || (canWithdraw && balance.kind === 'PRODUCT') ? (
        <IconButton
          label={balance.kind === 'PRODUCT' ? 'Retirar produto' : 'Usar sessão'}
          icon={balance.kind === 'PRODUCT' ? <PackageCheck /> : <CalendarCheck />}
          disabled={!activeUnitId || balance.status !== 'AVAILABLE' || balance.available < 1}
          onClick={() => setAction({ balance, type: 'USE' })}
        />
      ) : null}
      <IconButton
        label="Histórico e regras"
        icon={<History />}
        onClick={() => setDetails(balance)}
      />
      {canAdjust && balance.status !== 'CLOSED' ? (
        <>
          <IconButton
            label="Bonificar"
            icon={<Gift />}
            disabled={!activeUnitId}
            onClick={() => setAction({ balance, type: 'BONUS' })}
          />
          <IconButton
            label="Corrigir saldo"
            icon={<PencilLine />}
            disabled={!activeUnitId}
            onClick={() => setAction({ balance, type: 'CORRECTION' })}
          />
          <IconButton
            label="Prorrogar validade"
            icon={<CalendarPlus />}
            disabled={
              !activeUnitId ||
              !balance.expiresOn ||
              balance.rules.policy.extensionMode === 'DISABLED'
            }
            onClick={() => setAction({ balance, type: 'EXTEND' })}
          />
        </>
      ) : null}
    </div>
  );
  return (
    <section
      className="grid min-w-0 grid-cols-1 gap-4 [&_nav]:flex-wrap"
      aria-label="Saldos pré-pagos do cliente"
    >
      {balanceId ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-500/30 bg-brand-500/5 p-4 text-sm">
          <p>
            Produto selecionado no pacote contratado. Confira o item e use Retirar produto para
            registrar a entrega.
          </p>
          {onClearBalance ? (
            <Button variant="outline" onClick={onClearBalance}>
              Ver todos os produtos
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-content-muted">
          Cada item pertence a um pacote e a uma venda. Para entregar um produto, use a ação no item
          correspondente; não é necessário escolher o produto novamente.
        </p>
      </div>
      <div className="rounded-xl border border-surface-border bg-surface-card p-4 text-sm">
        <Button variant="ghost" aria-expanded={showHelp} onClick={() => setShowHelp(!showHelp)}>
          Entenda as ações do saldo
        </Button>
        {showHelp ? (
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            {Object.entries(actionHelp).map(([label, explanation]) => (
              <div key={label}>
                <dt className="flex items-center gap-2 font-semibold">
                  <span aria-hidden="true" className="text-brand-500 [&>svg]:size-4">
                    {actionIcons[label]}
                  </span>
                  {label}
                </dt>
                <dd className="mt-1 text-content-muted">{explanation}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
      <FilterBar
        searchId="prepaid-search"
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setOffset(0);
        }}
        searchPlaceholder={clientId ? 'Buscar pacote ou item' : 'Buscar cliente, pacote ou produto'}
        count={`${query.data?.total ?? 0} ${query.data?.total === 1 ? 'saldo' : 'saldos'}`}
        searchLabel="Buscar saldos"
        chips={[]}
        onClear={() => {
          setSearch('');
          setOffset(0);
        }}
        resultsLabel="Ver saldos"
        children={null}
        pageSize={limit}
        onPageSizeChange={(value) => {
          setLimit(value);
          setOffset(0);
        }}
      />
      {query.isLoading ? (
        <LoadingCard label="Carregando saldos" />
      ) : query.error ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : !balances.length ? (
        <EmptyCard
          icon={Package}
          title="Nenhum saldo encontrado"
          description="Busque pelo cliente, pacote ou item. Os saldos são criados a partir dos pacotes vendidos."
        />
      ) : view === 'table' ? (
        <div>
          <p className="mb-3 text-xs text-content-muted">
            Ordenação dos itens desta página. Use a busca para localizar em todos os saldos.
          </p>
          <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
            <CollectionCards>
              <div className="hue-collection-cards">
                <SortBar
                  label="Ordenação dos saldos"
                  columns={[...columns]}
                  sort={sort}
                  direction={direction}
                  onToggle={toggleSort}
                />
                <ul className="divide-y divide-surface-border">
                  {balances.map((balance) => (
                    <li key={balance.id} className="min-w-0 px-5 py-4">
                      <strong className="block truncate text-sm">{balance.itemName}</strong>
                      <p className="mt-1 text-xs text-content-muted">{balance.clientName}</p>
                      <p className="mt-1 text-xs text-content-muted">
                        {balance.packageName} · venda {balance.saleNumber} ·{' '}
                        {balance.businessUnitName}
                      </p>
                      <div className="mt-2">
                        <BalanceStatus balance={balance} />
                      </div>
                      <dl className="my-3 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <dt className="text-content-muted">Disponíveis</dt>
                          <dd className="font-semibold tabular-nums">
                            {balance.status === 'AVAILABLE' ? balance.available : 0}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-content-muted">Validade</dt>
                          <dd>
                            {balance.status === 'AWAITING'
                              ? 'A partir da liberação'
                              : day(balance.expiresOn)}
                          </dd>
                        </div>
                      </dl>
                      {actions(balance)}
                    </li>
                  ))}
                </ul>
              </div>
            </CollectionCards>
            <CollectionTable>
              <table className="w-full table-fixed border-collapse text-left">
                <caption className="sr-only">Saldos pré-pagos de pacotes vendidos</caption>
                <thead>
                  <tr className="border-b border-surface-border">
                    {columns.map((column) => (
                      <SortableColumn
                        key={column.sort}
                        column={column.sort}
                        label={column.label}
                        sort={sort}
                        direction={direction}
                        onToggle={toggleSort}
                      />
                    ))}
                    <th className="w-56">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {balances.map((balance) => (
                    <tr key={balance.id}>
                      <td className="px-5 py-4">
                        <strong className="block break-words text-sm">{balance.itemName}</strong>
                        <span className="mt-1 block break-words text-xs text-content-muted">
                          {balance.clientName}
                        </span>
                        <div className="mt-1">
                          <BalanceStatus balance={balance} />
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs text-content-muted">
                        {balance.packageName}
                        <span className="mt-1 block">
                          Venda {balance.saleNumber} · {balance.businessUnitName}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs font-semibold tabular-nums text-content">
                        {balance.status === 'AVAILABLE' ? balance.available : 0}
                        <span className="mt-1 block font-normal text-content-muted">
                          {balance.used} usadas · {balance.reserved ?? 0} reservadas
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-content-muted">
                        {balance.status === 'AWAITING'
                          ? 'A partir da liberação'
                          : day(balance.expiresOn)}
                      </td>
                      <td className="px-3 py-4">{actions(balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CollectionTable>
            {pagination}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {balances.map((balance) => (
            <article
              key={balance.id}
              className="grid content-start gap-4 rounded-2xl border border-surface-border bg-surface-card p-5"
            >
              <header>
                <p className="text-xs text-content-muted">
                  {balance.clientName ? `${balance.clientName} · ` : ''}
                  {balance.packageName} · venda {balance.saleNumber} · {balance.businessUnitName}
                </p>
                <h2 className="mt-1 text-xl font-semibold">{balance.itemName}</h2>
                <div className="mt-2">
                  <BalanceStatus balance={balance} />
                </div>
              </header>
              <dl className="grid grid-cols-2 gap-3 border-y border-surface-border py-4 text-sm">
                <div>
                  <dt className="text-content-muted">Disponíveis</dt>
                  <dd className="text-2xl font-bold">
                    {balance.status === 'AVAILABLE' ? balance.available : 0}
                  </dd>
                </div>
                {balance.reserved ? (
                  <div>
                    <dt className="text-content-muted">Reservadas na Agenda</dt>
                    <dd className="text-2xl font-bold">{balance.reserved}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-content-muted">Usadas</dt>
                  <dd className="text-2xl font-bold">{balance.used}</dd>
                </div>
                <div>
                  <dt className="text-content-muted">Liberadas / compradas</dt>
                  <dd>
                    {balance.released} / {balance.quantity}
                  </dd>
                </div>
                <div>
                  <dt className="text-content-muted">Vence em</dt>
                  <dd>
                    {balance.status === 'AWAITING' ? 'Conta da liberação' : day(balance.expiresOn)}
                  </dd>
                </div>
              </dl>
              {actions(balance)}
            </article>
          ))}
        </div>
      )}
      {view === 'cards' ? pagination : null}
      <Modal
        open={!!details}
        onOpenChange={(open) => {
          if (!open) setDetails(undefined);
        }}
        title="Histórico e regras do saldo"
      >
        {details ? (
          <div className="grid min-w-0 grid-cols-1 gap-5">
            <p>
              {details.packageName} · {details.itemName}
            </p>
            <h3 className="font-semibold">Regras preservadas da venda</h3>
            <dl className="grid min-w-0 grid-cols-1 gap-2 text-sm">
              {(Object.keys(ruleLabels) as Array<keyof typeof ruleLabels>).map((key) => (
                <div key={key} className="flex flex-wrap justify-between gap-2">
                  <dt>{ruleLabels[key]}</dt>
                  <dd>
                    {values[String(details.rules.policy[key])] ?? String(details.rules.policy[key])}{' '}
                    · {origins[details.rules.origins[key]]}
                  </dd>
                </div>
              ))}
            </dl>
            <h3 className="font-semibold">Histórico</h3>
            <BalanceHistory
              balance={details}
              canUse={canUse}
              activeUnitId={activeUnitId}
              onUndo={(eventId) => {
                setDetails(undefined);
                setAction({ balance: details, type: 'UNDO', eventId });
              }}
            />
          </div>
        ) : null}
      </Modal>
      {action ? (
        <ActionDialog
          key={`${action.balance.id}:${action.type}`}
          {...action}
          onClose={() => setAction(undefined)}
          onSaved={async () => {
            await client.invalidateQueries({ queryKey: ['company', 'prepaid'] });
            setAction(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

function ActionDialog({
  balance,
  type,
  eventId,
  onClose,
  onSaved,
}: {
  balance: PrepaidBalance;
  type: PrepaidAction['type'];
  eventId?: string | undefined;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [quantity, setQuantity] = useState(1);
  const [dateError, setDateError] = useState<string>();
  const [locationId, setLocationId] = useState('');
  const [lotId, setLotId] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const withdrawing = type === 'USE' && balance.kind === 'PRODUCT';
  const stock = useQuery({
    queryKey: ['company', 'prepaid', 'stock-options', balance.id, locationId, quantity],
    queryFn: () => prepaidApi.stockOptions(balance.id, locationId || undefined, quantity),
    enabled: withdrawing && Number.isSafeInteger(quantity) && quantity > 0,
    retry: false,
  });
  const stockLocations = stock.data?.locations;
  const onlyLocationId = stockLocations?.length === 1 ? stockLocations[0]!.id : undefined;
  if (onlyLocationId && locationId !== onlyLocationId) setLocationId(onlyLocationId);

  const [correctsId, setCorrectsId] = useState(balance.events[0]?.id ?? '');
  const [direction, setDirection] = useState('ADD');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const snackbar = useSnackbar();
  const title = {
    USE: withdrawing ? 'Retirar produto' : 'Usar sessão',
    UNDO: 'Desfazer uso',
    BONUS: 'Bonificar',
    CORRECTION: 'Corrigir saldo',
    EXTEND: 'Prorrogar validade',
  }[type];
  async function save() {
    setError(undefined);
    if (
      type === 'EXTEND' &&
      quantity >
        balance.rules.policy.extensionMonths -
          (balance.rules.policy.extensionMode === 'CUMULATIVE' ? balance.extensionMonthsUsed : 0)
    ) {
      setError('A prorrogação ultrapassa a regra deste saldo.');
      return;
    }
    if (dateError) {
      setError(dateError);
      return;
    }
    if (type !== 'USE' && !reason.trim()) {
      setError('Informe o motivo.');
      return;
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      setError('Informe uma quantidade inteira maior que zero.');
      return;
    }
    if (withdrawing && (!stock.data || !locationId || (stock.data.requiresLot && !lotId))) {
      setError('Escolha o local de estoque e o lote quando exigido.');
      return;
    }
    setBusy(true);
    try {
      await prepaidApi.act(balance.id, {
        type,
        quantity: type === 'CORRECTION' && direction === 'REMOVE' ? -quantity : quantity,
        reason,
        ...(withdrawing ? { locationId, idempotencyKey, ...(lotId ? { lotId } : {}) } : {}),
        ...(type === 'CORRECTION' ? { correctsId } : {}),
        ...(eventId ? { reversesId: eventId } : {}),
      });
      await onSaved();
      snackbar.success('Alteração registrada no histórico do saldo.');
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
      title={`${title} · ${balance.itemName}`}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            form="prepaid-action"
            disabled={busy || (withdrawing && stock.isFetching)}
          >
            {title}
          </Button>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <FormErrorSummary form="prepaid-action" />
        </div>
      }
    >
      <form
        id="prepaid-action"
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="rounded-xl border border-surface-border bg-surface-elevated p-4 text-sm">
          <p className="font-semibold">
            {balance.packageName} · venda {balance.saleNumber}
          </p>
          <p className="mt-2">
            {actionHelp[type === 'USE' ? (withdrawing ? 'Retirar produto' : 'Usar sessão') : title]}
          </p>
        </div>
        <p className="text-sm text-content-muted">
          Disponíveis: {balance.available} · validade: {day(balance.expiresOn)}
        </p>
        {type === 'EXTEND' ? (
          <p className="text-sm">
            Limite: {balance.rules.policy.extensionMonths} meses ·{' '}
            {balance.rules.policy.extensionMode === 'CUMULATIVE'
              ? `${balance.extensionMonthsUsed} já utilizados no total`
              : 'por solicitação'}
          </p>
        ) : null}
        {type === 'CORRECTION' ? (
          <Select
            label="Fato a corrigir"
            value={correctsId}
            onValueChange={setCorrectsId}
            options={balance.events.map((event) => ({
              value: event.id,
              label: `${types[event.type]} · ${event.quantity} · ${event.reason ?? event.actorName}`,
            }))}
          />
        ) : null}
        {type === 'CORRECTION' ? (
          <Select
            label="Ajuste"
            value={direction}
            onValueChange={setDirection}
            options={[
              { value: 'ADD', label: 'Acrescentar' },
              { value: 'REMOVE', label: 'Retirar' },
            ]}
          />
        ) : null}
        {type !== 'UNDO' ? (
          <Input
            className="w-28 max-w-full"
            label={type === 'EXTEND' ? 'Meses a mais' : 'Quantidade'}
            inputMode="numeric"
            value={String(quantity)}
            onChange={(event) => {
              setQuantity(Number(event.target.value));
              setDateError(undefined);
            }}
          />
        ) : (
          <p>O uso original será desfeito por inteiro. O histórico será preservado.</p>
        )}
        {type === 'EXTEND' &&
        balance.expiresOn &&
        Number.isSafeInteger(quantity) &&
        quantity > 0 ? (
          <DatePicker
            label="Nova validade"
            value={addMonths(balance.expiresOn, quantity)}
            today={civilDateOf(new Date(), balance.timeZone)}
            hint="A regra deste saldo permite meses inteiros. Escolha a data correspondente à quantidade de meses, preservando o dia do vencimento (ou o último dia do mês)."
            error={dateError}
            onValueChange={(date) => {
              const months = Array.from({ length: 24 }, (_, index) => index + 1).find(
                (n) => addMonths(balance.expiresOn!, n) === date,
              );
              if (months) {
                setQuantity(months);
                setDateError(undefined);
              } else
                setDateError(
                  'Escolha uma data correspondente a meses inteiros após a validade atual.',
                );
            }}
          />
        ) : null}
        {withdrawing ? (
          <>
            {stockLocations?.length === 0 ? <NoStockLocationNotice /> : null}
            <Select
              label="Local de estoque"
              value={locationId}
              onValueChange={(value) => {
                setLocationId(value);
                setLotId('');
              }}
              options={[
                { value: '', label: 'Escolha o estoque' },
                ...(stock.data?.locations ?? []).map((l) => ({
                  value: l.id,
                  label: l.displayName,
                })),
              ]}
            />
            {stock.data?.requiresLot ? (
              <Select
                label="Lote"
                value={lotId}
                onValueChange={setLotId}
                options={[
                  { value: '', label: 'Escolha o lote' },
                  ...stock.data.lots.map((l) => ({
                    value: l.id,
                    label: `${l.code}${l.suggested ? ' · sugerido' : ''} · saldo ${l.balance}`,
                  })),
                ]}
              />
            ) : null}
            {stock.error ? <ErrorCard message={getApiError(stock.error)} /> : null}
            <p className="text-sm text-content-muted">
              A retirada desconta o pacote e baixa o estoque juntos. Desfazer devolve ambos.
            </p>
          </>
        ) : null}
        <Textarea
          label={type === 'USE' ? 'Observação (opcional)' : 'Motivo'}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-invalid={type !== 'USE' && !!error && !reason.trim()}
        />
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

function BalanceHistory({
  balance,
  canUse,
  activeUnitId,
  onUndo,
}: {
  balance: PrepaidBalance;
  canUse: boolean;
  activeUnitId: string | null;
  onUndo: (id: string) => void;
}) {
  const [offset, setOffset] = useState(0);
  const history = useQuery({
    queryKey: ['company', 'prepaid', 'history', balance.id, offset],
    queryFn: () => prepaidApi.history(balance.id, offset),
    placeholderData: keepPreviousData,
    retry: false,
  });
  if (history.isLoading) return <LoadingCard label="Carregando histórico" />;
  if (!history.data) return <ErrorCard message={getApiError(history.error)} />;
  return (
    <div className="grid min-w-0 grid-cols-1 gap-3">
      {history.data.events.map((event) => (
        <div key={event.id} className="border-t border-surface-border pt-3 text-sm">
          <p className="font-semibold">
            {types[event.type]} {event.quantity ? `· ${event.quantity}` : ''}
            {event.reversed ? ' · desfeito' : ''}
          </p>
          <p className="text-content-muted">
            {formatDateTime(event.createdAt, event.timeZone ?? balance.timeZone)} ·{' '}
            {event.actorName} · {event.businessUnitName}
          </p>
          {event.reason ? <p>{event.reason}</p> : null}
          {canUse &&
          activeUnitId === event.businessUnitId &&
          event.type === 'USE' &&
          !event.reversed &&
          balance.status !== 'CLOSED' ? (
            <IconButton
              label="Desfazer uso"
              icon={<Undo2 />}
              tone="danger"
              onClick={() => onUndo(event.id)}
            />
          ) : null}
        </div>
      ))}
      <Pagination
        total={history.data.total}
        limit={25}
        offset={offset}
        onOffsetChange={setOffset}
        itemLabel={(count) => (count === 1 ? 'registro' : 'registros')}
      />
    </div>
  );
}
